/**
 * Camera capture → compress → upload → preview, in one reusable block.
 *
 * Used by task completion (`evidenceFileId`), KPI appeals (`evidenceFileId`)
 * and leave requests (`attachmentFileId`). The parent only ever receives the
 * server-issued `fileId` (or null); everything about bytes, progress and retry
 * lives here so three screens cannot drift.
 *
 * HONESTY RULES:
 *  - the "đã tải lên" state is only shown after the server answered 201 AND the
 *    preview below it is fetched back from `GET /files/:id` — the photo on
 *    screen is the server's copy, not the local one, so what the employee sees
 *    is exactly what a manager reviewing the task will see;
 *  - a failed upload keeps the compressed photo and offers "Thử lại" with the
 *    same bytes — no silent drop, no pretending it went through;
 *  - offline, the button says so instead of spinning. Uploads are not queued in
 *    the outbox: a completion queued offline goes WITHOUT a photo and the KPI
 *    consequence (MISSING_EVIDENCE) is explained by the calling screen.
 */
import { useEffect, useRef, useState } from 'react';
import { compressImage, fetchFileObjectUrl, uploadFile, type UploadPurpose } from '../lib/upload';
import { useSyncState } from '../lib/hooks';
import { Button } from './ui';

type Phase =
  | { readonly step: 'idle' }
  | { readonly step: 'compressing' }
  | { readonly step: 'uploading'; readonly fraction: number; readonly blob: Blob }
  | { readonly step: 'error'; readonly message: string; readonly blob: Blob; readonly localUrl: string }
  | { readonly step: 'done'; readonly fileId: string; readonly previewUrl: string; readonly fromServer: boolean };

export function EvidenceUploader({
  label,
  hint,
  purpose = 'EVIDENCE',
  disabled = false,
  onChange,
  onBusyChange,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly purpose?: UploadPurpose;
  readonly disabled?: boolean;
  /** Fired with the server fileId on success, and with null on remove/retake. */
  readonly onChange: (fileId: string | null) => void;
  /** True while compressing/uploading — lets the parent hold its submit button. */
  readonly onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ step: 'idle' });
  const fileInput = useRef<HTMLInputElement | null>(null);
  const objectUrls = useRef<string[]>([]);
  const { online } = useSyncState();

  // Every object URL ever handed to the DOM is revoked on unmount; a shift's
  // worth of retaken photos must not pin megabytes of blobs in memory.
  useEffect(
    () => () => {
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const track = (url: string): string => {
    objectUrls.current.push(url);
    return url;
  };

  const startUpload = async (blob: Blob): Promise<void> => {
    setPhase({ step: 'uploading', fraction: 0, blob });
    try {
      const uploaded = await uploadFile(blob, purpose, `evidence-${Date.now()}.jpg`, (fraction) => {
        setPhase((prev) => (prev.step === 'uploading' ? { ...prev, fraction } : prev));
      });
      // Prove the round trip: preview the SERVER's copy via GET /files/:id.
      let previewUrl: string;
      let fromServer = true;
      try {
        previewUrl = track(await fetchFileObjectUrl(uploaded.fileId));
      } catch {
        previewUrl = track(URL.createObjectURL(blob));
        fromServer = false;
      }
      setPhase({ step: 'done', fileId: uploaded.fileId, previewUrl, fromServer });
      onChange(uploaded.fileId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không tải được ảnh lên.';
      setPhase({ step: 'error', message, blob, localUrl: track(URL.createObjectURL(blob)) });
      onChange(null);
    }
  };

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    // Allow re-picking the same file name.
    event.target.value = '';
    if (file === undefined) return;
    onChange(null);
    setPhase({ step: 'compressing' });
    try {
      const compressed = await compressImage(file);
      await startUpload(compressed);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Không xử lý được ảnh.';
      setPhase({ step: 'error', message, blob: file, localUrl: track(URL.createObjectURL(file)) });
    }
  };

  const retake = (): void => {
    onChange(null);
    setPhase({ step: 'idle' });
    fileInput.current?.click();
  };

  const busy = phase.step === 'compressing' || phase.step === 'uploading';

  useEffect(() => {
    onBusyChange?.(busy);
    // `onBusyChange` is intentionally omitted: parents pass inline lambdas and
    // re-running on every render would defeat the effect. Busy is the signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  return (
    <div>
      <p className="label">{label}</p>
      {hint === undefined ? null : <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}

      {/* `capture="environment"` opens the rear camera directly on Android and
          iOS instead of the photo library — one tap instead of four. */}
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => void onPick(event)}
      />

      {phase.step === 'idle' ? (
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          className="mt-2"
          disabled={disabled || !online}
          onClick={() => fileInput.current?.click()}
        >
          Chụp ảnh
        </Button>
      ) : null}
      {!online && phase.step === 'idle' ? (
        <p className="mt-2 rounded-xl bg-slate-100 p-3 text-xs text-slate-600">
          Đang ngoại tuyến — cần có mạng để tải ảnh lên máy chủ.
        </p>
      ) : null}

      {phase.step === 'compressing' ? (
        <p className="mt-2 rounded-xl bg-slate-100 p-3 text-sm text-slate-700">Đang nén ảnh…</p>
      ) : null}

      {phase.step === 'uploading' ? (
        <div className="mt-2 rounded-xl bg-slate-100 p-3">
          <div className="flex items-center justify-between text-sm text-slate-700">
            <span>Đang tải ảnh lên…</span>
            <span className="tabular-nums font-semibold">{Math.round(phase.fraction * 100)}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-brand-600 transition-[width]"
              style={{ width: `${Math.round(phase.fraction * 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      {phase.step === 'error' ? (
        <div className="mt-2 rounded-xl border border-rose-300 bg-rose-50 p-3">
          <p className="text-sm font-semibold text-rose-900">Chưa tải được ảnh lên</p>
          <p className="mt-1 text-sm text-rose-800">{phase.message}</p>
          <img src={phase.localUrl} alt="Ảnh đã chụp (chưa tải lên)" className="mt-2 w-full rounded-xl object-cover" />
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button variant="secondary" size="lg" onClick={retake} disabled={busy}>
              Chụp lại
            </Button>
            <Button size="lg" onClick={() => void startUpload(phase.blob)} disabled={!online} loading={busy}>
              Thử lại
            </Button>
          </div>
        </div>
      ) : null}

      {phase.step === 'done' ? (
        <div className="mt-2">
          <p className="rounded-xl bg-emerald-50 p-2 text-sm font-semibold text-emerald-800">
            {phase.fromServer
              ? 'Ảnh đã ở trên máy chủ — bên dưới là bản máy chủ trả về.'
              : 'Ảnh đã tải lên (chưa tải lại được bản xem trước từ máy chủ).'}
          </p>
          <img src={phase.previewUrl} alt="Ảnh bằng chứng đã tải lên" className="mt-2 w-full rounded-2xl object-cover" />
          <Button variant="secondary" size="lg" fullWidth className="mt-2" onClick={retake}>
            Chụp lại
          </Button>
        </div>
      ) : null}
    </div>
  );
}
